import React, { useEffect, useMemo, useState } from "react";
import { BRAND } from "@/lib/constants";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function composeSlides(data) {
  if (!data) return [];
  const custom = (data.slides || []).filter(s => s.active).map(s => ({ ...s, image_url: `${BACKEND}${s.image_url}` }));
  const defaults = data.show_defaults === false ? [] : (data.defaults || []);
  return [...custom, ...defaults];
}

/** Idle-screen promo slideshow — fades between slides every `intervalMs`. */
export default function PromoSlideshow({ data }) {
  const slides = useMemo(() => composeSlides(data), [data]);
  const intervalMs = Math.max(3, data?.interval_seconds || 8) * 1000;
  const [idx, setIdx] = useState(0);

  useEffect(() => { setIdx(0); }, [slides.length]);
  useEffect(() => {
    if (slides.length < 2) return;
    const id = setInterval(() => setIdx(i => (i + 1) % slides.length), intervalMs);
    return () => clearInterval(id);
  }, [slides.length, intervalMs]);

  if (slides.length === 0) {
    return (
      <div data-testid="promo-slideshow" className="h-full flex items-center justify-center text-center p-12">
        <div>
          <img src="/logos/monogram.png" alt="" className="w-24 h-24 mx-auto mb-6 object-contain"/>
          <h1 className="font-[Outfit] text-5xl md:text-6xl font-light tracking-tighter text-[#F5F2EB]">{BRAND.name}</h1>
          <p className="overline mt-4">{BRAND.tagline}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="promo-slideshow" className="relative h-full overflow-hidden bg-black">
      {slides.map((s, i) => (
        <div key={s.slide_id} data-testid={`promo-slide-${s.slide_id}`} aria-hidden={i !== idx}
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: i === idx ? 1 : 0 }}>
          <img src={s.image_url} alt="" className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: i === idx ? "scale(1.06)" : "scale(1)", transition: `transform ${intervalMs + 1000}ms linear` }}/>
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.25) 0%, rgba(5,5,5,0.15) 45%, rgba(5,5,5,0.9) 100%)" }}/>
          <div className="absolute left-10 right-10 bottom-16 md:left-16 md:bottom-20">
            <div className="flex items-center gap-3 mb-6">
              <img src="/logos/monogram.png" alt="" className="w-10 h-10 object-contain"/>
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">{BRAND.name}</span>
            </div>
            {s.title && <h1 className="font-[Outfit] text-5xl md:text-6xl xl:text-7xl font-light tracking-tighter leading-[1.02] text-[#F5F2EB] max-w-4xl" data-testid="promo-title">{s.title}</h1>}
            {s.subtitle && <p className="mt-5 text-lg md:text-xl text-[#E4E4E7]/85 max-w-2xl" data-testid="promo-subtitle">{s.subtitle}</p>}
          </div>
        </div>
      ))}
      <div className="absolute bottom-6 left-10 md:left-16 flex gap-2" data-testid="promo-dots">
        {slides.map((s, i) => <span key={s.slide_id} className="h-[3px] transition-all duration-500" style={{ width: i === idx ? 36 : 14, background: i === idx ? "#D4AF37" : "rgba(255,255,255,0.3)" }}/>)}
      </div>
      <div className="absolute bottom-6 right-10 text-sm text-[#A1A1AA]">{BRAND.phones[0]} · {BRAND.area}</div>
    </div>
  );
}
